import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, CheckCircle2, Quote } from "lucide-react";
import { KineticLines, Reveal, Counter, Eyebrow } from "@/components/site/Reveal";
import { FunnelVisual } from "@/components/site/FunnelVisual";
import { FunnelSelector } from "@/components/site/FunnelSelector";
import { ReachCalculator } from "@/components/site/ReachCalculator";
import { LeadDrawer } from "@/components/site/LeadDrawer";
import { ProposalForm } from "@/components/site/ProposalForm";
import { Marquee } from "@/components/site/Marquee";
import { SOLUTIONS, METRICS, TESTIMONIALS } from "@/data/content";

const HERO_BULLETS = [
  { t: "Full-Funnel Coverage", d: "From brand awareness and MQL content opt-ins to rigorous BANT & Confirmed Callback (CCL) opportunities." },
  { t: "Agency-Grade Reliability", d: "100% allocation fulfillment, guaranteed zero data attrition, and custom-tailored campaign parameters." },
  { t: "Global Footprint", d: "Seamless execution across North America, EMEA, APAC, and LATAM in 90+ countries." },
];

const TRUST_BADGES = [
  "100% CCPA / CPRA COMPLIANT",
  "GDPR & CASL READY",
  "TRIPLE-TOUCH VERIFIED PHONE & EMAIL",
  "ZERO-BOUNCE SMTP STANDARD",
];

const WHY = [
  { t: "Agency-Native Empathy & Dedicated Account Pods", d: "We understand the pressures B2B media agencies face: strict allocation deadlines, changing client specs, and inflexible quality thresholds. Our dedicated campaign managers act as a seamless white-label delivery engine for your team." },
  { t: "Triple-Touch Quality Verification", d: "Every lead is screened through a 3-layer validation process: automated syntax and domain verification, digital engagement telemetry, and human tele-qualification to ensure 100% data authenticity." },
  { t: "Proprietary First-Party Audience", d: "Unlike brokers who repackage stale third-party scrapings, Vyacomm Media connects you with active professionals who engage with our proprietary publication network and opted-in research hubs." },
  { t: "Full Regulatory Peace of Mind", d: "Our data processes comply strictly with GDPR, CCPA, CASL, and regional privacy frameworks. Complete opt-in audit trails and time-stamped consent records are available for every delivery." },
];

export default function Home() {
  useEffect(() => {
    document.title = "Vyacomm Media | Global B2B Demand Generation & Tech Media Publisher";
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="grain hero-grid-lines relative overflow-hidden bg-navy pt-[160px]" data-testid="home-hero">
        <div className="pointer-events-none absolute -left-40 top-20 h-[480px] w-[480px] rounded-full bg-cobalt/20 blur-[140px]" />
        <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative z-10 mx-auto grid max-w-[1808px] items-center gap-14 px-5 pb-24 sm:px-8 lg:grid-cols-2 lg:px-12 2xl:px-14">
          <div>
            <Reveal delay={0.05} y={16}>
              <Eyebrow dark>Precision Demand Generation for Enterprise Tech & Media Agencies</Eyebrow>
            </Reveal>
            <h1 className="mt-6 font-jakarta text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-[64px]">
              <KineticLines
                lines={["We Turn In-Market", "Tech Buyers Into", "Predictable Pipeline."]}
                accentIndex={2}
              />
            </h1>
            <Reveal delay={0.6}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                Vyacomm Media unites an audited database of 140M+ global decision-makers, proprietary intent
                intelligence, and multi-channel content syndication to deliver fully qualified, sales-ready
                opportunities with guaranteed volume and quality.
              </p>
            </Reveal>
            <div className="mt-8 space-y-4">
              {HERO_BULLETS.map((b, i) => (
                <Reveal key={b.t} delay={0.72 + i * 0.1} y={14}>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-flame" />
                    <p className="text-sm leading-relaxed text-slate-300">
                      <span className="font-semibold text-white">{b.t} — </span>
                      {b.d}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={1.05}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact-us"
                  data-testid="hero-launch-campaign-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-cobalt px-7 py-4 text-sm font-bold text-white shadow-[0_18px_44px_-12px_rgba(37,99,235,0.8)] transition-all duration-300 hover:scale-[1.03] hover:bg-[#1d4fd7] active:scale-95"
                >
                  Launch Your Campaign
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
              <p className="mt-4 font-mono2 text-[10px] tracking-[0.18em] text-slate-400">
                ZERO OBLIGATION · FEASIBILITY IN 24 HOURS · 100% OPT-IN VERIFIED
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.4} y={40} className="hidden lg:block">
            <FunnelVisual />
          </Reveal>
        </div>

        {/* TRUST STRIP */}
        <div className="relative z-10 border-t border-white/10 bg-[#081426]">
          <div className="mx-auto flex max-w-[1808px] flex-wrap items-center justify-center gap-x-10 gap-y-3 px-5 py-5 sm:px-8 lg:px-12 2xl:px-14">
            {TRUST_BADGES.map((b) => (
              <span key={b} className="flex items-center gap-2 font-mono2 text-[10px] tracking-[0.18em] text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5 text-cobaltlight" />
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="border-b border-line bg-white" data-testid="metrics-bar">
        <div className="mx-auto grid max-w-[1808px] grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          {METRICS.map((m, i) => (
            <div
              key={m.label}
              className={`border-line px-6 py-10 ${i % 2 === 0 ? "border-r" : ""} ${i < 4 ? "border-b lg:border-b-0" : ""} lg:border-r lg:last:border-r-0`}
            >
              <Reveal delay={i * 0.06}>
                <p className="font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                  <Counter to={m.value} suffix={m.suffix} decimals={m.decimals || 0} />
                </p>
                <p className="mt-2 text-xs leading-snug text-slate-500">{m.label}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* FUNNEL SELECTOR */}
      <section className="bg-canvas py-24 sm:py-32" data-testid="funnel-section">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>The Full-Funnel Advantage</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Demand generation built for every stage of the complex B2B buying journey
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
              In enterprise B2B sales cycles spanning 6 to 18 months, traditional single-touch leads no longer convert.
              Vyacomm Media engineers targeted engagement workflows that capture curiosity, nurture intent, and convert
              evaluation into committed sales conversations.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <FunnelSelector />
          </Reveal>
        </div>
      </section>

      {/* BENTO SOLUTIONS */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="solutions-bento">
        <div className="pointer-events-none absolute right-0 top-0 h-[400px] w-[400px] rounded-full bg-cobalt/15 blur-[140px]" />
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow dark>Core Capabilities</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-jakarta text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Engineered for performance: our suite of B2B pipeline solutions
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SOLUTIONS.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07} className={s.big ? "lg:col-span-2" : ""}>
                <Link
                  to={`/solutions/${s.id}`}
                  data-testid={`bento-solution-${s.id}`}
                  className="group flex h-full flex-col justify-between rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-cobalt/60 hover:bg-white/[0.07]"
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="grid h-11 w-11 place-items-center rounded-xl bg-cobalt/15 text-cobaltlight transition-colors duration-300 group-hover:bg-cobalt group-hover:text-white">
                        <s.icon className="h-5 w-5" />
                      </span>
                      <span className="font-mono2 text-xs tracking-[0.2em] text-slate-500">{s.num}</span>
                    </div>
                    <h3 className="mt-5 font-jakarta text-xl font-bold text-white">{s.title}</h3>
                    <p className="mt-1 font-mono2 text-[10px] tracking-[0.18em] text-flame">{s.tag.toUpperCase()}</p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.short}</p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-cobaltlight transition-colors group-hover:text-white">
                    Explore capability
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REACH CALCULATOR */}
      <section className="grain relative overflow-hidden bg-eblue py-24 sm:py-32" data-testid="reach-section">
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow dark>Audience Reach Calculator</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-jakarta text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Size your total addressable audience in seconds
            </h2>
            <p className="mt-5 max-w-2xl text-base text-slate-300">
              Select your target region, seniority, and vertical — we will render a live feasibility estimate from our
              audited universe of 140M+ verified decision-makers.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <ReachCalculator />
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* WHY VYACOMM */}
      <section className="bg-canvas py-24 sm:py-32" data-testid="why-section">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <Eyebrow>Why Vyacomm</Eyebrow>
                <h2 className="mt-4 font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                  Built specifically for the complexities of B2B media
                </h2>
                <div className="mt-8">
                  <LeadDrawer />
                </div>
              </Reveal>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
              {WHY.map((w, i) => (
                <Reveal key={w.t} delay={i * 0.08}>
                  <div className="h-full rounded-2xl border border-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-cobalt/50 hover:shadow-[0_20px_50px_-24px_rgba(11,25,44,0.25)]">
                    <span className="font-mono2 text-xs tracking-[0.2em] text-cobalt">0{i + 1}</span>
                    <h3 className="mt-3 font-jakarta text-lg font-bold text-navy">{w.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y border-line bg-white py-24 sm:py-32" data-testid="testimonials-section">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>Client Proof</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Trusted by the teams who cannot afford a bad lead
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <figure className="flex h-full flex-col rounded-2xl border border-line bg-canvas p-8">
                  <Quote className="h-7 w-7 text-cobalt" />
                  <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-charcoal">“{t.quote}”</blockquote>
                  <figcaption className="mt-7 border-t border-line pt-5">
                    <span className="font-mono2 text-[9px] tracking-[0.22em] text-flame">{t.tag}</span>
                    <p className="mt-1.5 font-jakarta text-sm font-bold text-navy">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.org}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA + FORM */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="home-cta-section">
        <div className="pointer-events-none absolute -left-32 bottom-0 h-[380px] w-[380px] rounded-full bg-flame/10 blur-[130px]" />
        <div className="relative z-10 mx-auto grid max-w-[1808px] gap-14 px-5 sm:px-8 lg:px-12 2xl:px-14 lg:grid-cols-2">
          <Reveal>
            <Eyebrow dark>Start Your Program</Eyebrow>
            <h2 className="mt-4 font-jakarta text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Ready to transform your B2B growth trajectory?
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
              Whether you are launching an international content syndication blitz, activating a target account list, or
              requiring sales-ready BANT leads — Vyacomm Media delivers the precision, scale, and reliability you need.
            </p>
            <div className="mt-10 space-y-5">
              {[
                "Dedicated Account Strategist assigned within 2 business hours",
                "Custom TAM count and CPL pricing delivered within 24 hours",
                "Zero spam policy — we only engage opted-in, verified audiences",
              ].map((step, i) => (
                <div key={step} className="flex items-center gap-4">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-flame/50 font-mono2 text-xs font-semibold text-flame">
                    {i + 1}
                  </span>
                  <p className="text-sm text-slate-200">{step}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-white/10 bg-white p-7 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)] sm:p-9">
              <p className="font-mono2 text-[10px] tracking-[0.24em] text-cobalt">REQUEST AUDIENCE COUNT & PROPOSAL</p>
              <h3 className="mt-2 font-jakarta text-2xl font-extrabold text-navy">Campaign feasibility in 24 hours</h3>
              <div className="mt-6">
                <ProposalForm testIdPrefix="home-proposal" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
